import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  unstable_useBlocker as useBlocker,
  useNavigate,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";

import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { useLazyGetStatusesQuery } from "../../../../slices/StatusesSlice";
import {
  UpdateWorkTypeStatusModel,
  useActivateWorkTypeMutation,
  useAddWorkTypeMutation,
  useDeactivateWorkTypeMutation,
  useDeleteWorkTypeMutation,
  useLazyGetWorkTypeQuery,
  useUpdateWorkTypeBillableDefaultMutation,
  useUpdateWorkTypeDefaultDataMutation,
  useUpdateWorkTypeDefaultDueDaysMutation,
  useUpdateWorkTypeMutation,
  useUpdateWorkTypeStatusesMutation,
} from "../../../../slices/WorkTypeSlice";
import { dueDaysToInput, parseDueDays } from "../../../../utils/dueDays";
import {
  WorkTypeEditorController,
  WorkTypeFormData,
  WorkTypeNavigationBlocker,
} from "../model/workTypeEditor.types";
import {
  fromBillableChoice,
  getWorkTypeStatusesSignature,
  HIDDEN_WORK_TYPE_STATUS_NAMES,
  serverErrorMessage,
  toBillableChoice,
} from "../model/workTypeEditor.utils";

export const useWorkTypeEditorController = (): WorkTypeEditorController => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditing = Boolean(id && id !== "add");

  const { control, handleSubmit, setValue } = useForm<WorkTypeFormData>({
    defaultValues: {
      name: "",
      description: "",
      defaultTemplateId: null,
      defaultAssigneeId: null,
      defaultRoleId: null,
      defaultBudget: "0",
      billableDefault: "none",
      defaultDueDays: "",
    },
  });
  const formValues = useWatch({ control });

  const [openWorkTypeDelete, setOpenWorkTypeDelete] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const shouldBypassUnsavedPromptRef = useRef(false);

  const [workTypeStatuses, setWorkTypeStatuses] = useState<
    UpdateWorkTypeStatusModel[]
  >([]);
  const [draftIsActive, setDraftIsActive] = useState(true);

  const [getWorkType, { data: workType, isLoading }] =
    useLazyGetWorkTypeQuery();
  const [getStatuses, { isLoading: isLoadingStatuses, data: statuses }] =
    useLazyGetStatusesQuery();
  const [updateStatuses, { isLoading: isLoadingUpdateStatuses }] =
    useUpdateWorkTypeStatusesMutation();
  const [updateDefaultData, { isLoading: isLoadingUpdateDefaultData }] =
    useUpdateWorkTypeDefaultDataMutation();
  const [updateBillableDefault, { isLoading: isLoadingBillableDefault }] =
    useUpdateWorkTypeBillableDefaultMutation();
  const [updateDefaultDueDays, { isLoading: isLoadingDefaultDueDays }] =
    useUpdateWorkTypeDefaultDueDaysMutation();

  const [updateWorkType, { isLoading: isLoadingUpdate }] =
    useUpdateWorkTypeMutation();
  const [addWorkType, { isLoading: isLoadingAdd }] = useAddWorkTypeMutation();
  const [deleteWorkType, { isLoading: isLoadingDelete }] =
    useDeleteWorkTypeMutation();
  const [deactivateWorkType, { isLoading: isLoadingDeactivate }] =
    useDeactivateWorkTypeMutation();
  const [activateWorkType, { isLoading: isLoadingActivate }] =
    useActivateWorkTypeMutation();

  const visibleServiceStatuses = useMemo(
    () =>
      workType?.serviceStatuses.filter(
        (status) => !HIDDEN_WORK_TYPE_STATUS_NAMES.includes(status.name),
      ) || [],
    [workType?.serviceStatuses],
  );

  const getEditableWorkTypeStatuses = useCallback(
    () =>
      visibleServiceStatuses
        .filter((item) => Boolean(item.workTypeStatusId))
        .map((item) => ({
          workTypeStatusId: item.workTypeStatusId,
          workTypeSubStatusIds: item.serviceSubStatuses.map(
            (sub) => sub.worktypeSubStatusId,
          ),
        })),
    [visibleServiceStatuses],
  );

  const initialWorkTypeStatusesSignature = useMemo(
    () => getWorkTypeStatusesSignature(getEditableWorkTypeStatuses()),
    [getEditableWorkTypeStatuses],
  );
  const draftWorkTypeStatusesSignature = useMemo(
    () => getWorkTypeStatusesSignature(workTypeStatuses),
    [workTypeStatuses],
  );

  const initialDefaultBudget =
    workType?.defaultWorkTypeData?.defaultBudget === null ||
    workType?.defaultWorkTypeData?.defaultBudget === undefined
      ? "0"
      : String(workType?.defaultWorkTypeData?.defaultBudget);
  const draftDefaultBudget = formValues.defaultBudget || "0";

  const isDetailsChanged =
    (formValues.name ?? "") !== (isEditing ? workType?.name || "" : "") ||
    (formValues.description ?? "") !==
      (isEditing ? workType?.description || "" : "");
  const areDefaultValuesChanged =
    isEditing &&
    ((formValues.defaultTemplateId ?? null) !==
      (workType?.defaultWorkTypeData?.defaultTemplateId ?? null) ||
      (formValues.defaultAssigneeId ?? null) !==
        (workType?.defaultWorkTypeData?.defaultAssigneeId ?? null) ||
      (formValues.defaultRoleId ?? null) !==
        (workType?.defaultWorkTypeData?.defaultRoleId ?? null) ||
      draftDefaultBudget !== initialDefaultBudget);
  const isBillableDefaultChanged =
    isEditing &&
    (formValues.billableDefault ?? "none") !==
      toBillableChoice(workType?.billableDefault);
  const isDefaultDueDaysChanged =
    isEditing &&
    (formValues.defaultDueDays ?? "").trim() !==
      dueDaysToInput(workType?.noOfDays);
  const isActiveChanged = isEditing && draftIsActive !== workType?.isActive;
  const areStatusesChanged =
    isEditing &&
    draftWorkTypeStatusesSignature !== initialWorkTypeStatusesSignature;

  const hasUnsavedChanges =
    (!isEditing || Boolean(workType)) &&
    (isDetailsChanged ||
      areDefaultValuesChanged ||
      isBillableDefaultChanged ||
      isDefaultDueDaysChanged ||
      isActiveChanged ||
      areStatusesChanged);

  const shouldBlockNavigation =
    hasUnsavedChanges &&
    !isSavingDraft &&
    !shouldBypassUnsavedPromptRef.current;
  const blocker = useBlocker(
    shouldBlockNavigation,
  ) as WorkTypeNavigationBlocker;

  const fetchWorkType = useCallback(async () => {
    if (isEditing && id) {
      await getWorkType({ id }).unwrap();
    }
  }, [getWorkType, id, isEditing]);

  const fetchStatuses = useCallback(async () => {
    return getStatuses({ orgId: getLinkedFirm()?.orgId }).unwrap();
  }, [getStatuses]);

  const onSubmit = async (formData: WorkTypeFormData) => {
    setIsSavingDraft(true);

    if (isEditing && workType) {
      let shouldRefreshWorkType = false;

      // Check the days before saving anything (Roadmap 1849).
      const dueDaysCheck = parseDueDays(formData.defaultDueDays);

      if (isDefaultDueDaysChanged && !dueDaysCheck.ok) {
        toast(`Default due date: ${dueDaysCheck.error}`, { type: "error" });
        setIsSavingDraft(false);
        return;
      }

      if (isDetailsChanged) {
        try {
          await updateWorkType({
            id: workType.id,
            name: formData.name,
            description: formData.description,
          }).unwrap();
          shouldRefreshWorkType = true;
          toast("Work type successfully updated", {
            type: "success",
          });
        } catch (error) {
          toast(serverErrorMessage(error, "Something went wrong"), {
            type: "error",
          });
          setIsSavingDraft(false);
          return;
        }
      }

      if (areStatusesChanged) {
        try {
          await updateStatuses({
            workTypeId: workType.id,
            statuses: workTypeStatuses.filter((status) =>
              Boolean(status.workTypeStatusId),
            ),
          }).unwrap();
          shouldRefreshWorkType = true;
          toast("Work type statuses successfully saved", {
            type: "success",
          });
        } catch (error) {
          // QA batch 17: "“X” is used by N work items… Move them first." from the server.
          toast(
            serverErrorMessage(
              error,
              "Something went wrong while updating statuses",
            ),
            { type: "error" },
          );
          setIsSavingDraft(false);
          return;
        }
      }

      if (areDefaultValuesChanged) {
        try {
          const defaultBudget = Number(formData.defaultBudget);

          await updateDefaultData({
            workTypeId: workType.id,
            defaultTemplateId: formData.defaultTemplateId || null,
            defaultAssigneeId: formData.defaultAssigneeId || null,
            defaultRoleId: formData.defaultRoleId || null,
            defaultBudget: Number.isFinite(defaultBudget)
              ? Math.max(defaultBudget, 0)
              : null,
          }).unwrap();
          shouldRefreshWorkType = true;
          toast("Work type default values successfully saved", {
            type: "success",
          });
        } catch {
          toast("Something went wrong while updating default values", {
            type: "error",
          });
          setIsSavingDraft(false);
          return;
        }
      }

      if (isBillableDefaultChanged) {
        try {
          await updateBillableDefault({
            id: workType.id,
            billableDefault: fromBillableChoice(formData.billableDefault),
          }).unwrap();
          shouldRefreshWorkType = true;
          toast("Time billing default successfully saved", {
            type: "success",
          });
        } catch {
          toast("Something went wrong while saving the time billing default", {
            type: "error",
          });
          setIsSavingDraft(false);
          return;
        }
      }

      if (isDefaultDueDaysChanged) {
        const parsed = parseDueDays(formData.defaultDueDays);

        if (!parsed.ok) {
          toast(`Default due date: ${parsed.error}`, { type: "error" });
          setIsSavingDraft(false);
          return;
        }

        try {
          await updateDefaultDueDays({
            id: workType.id,
            defaultDueDays: parsed.value,
          }).unwrap();
          shouldRefreshWorkType = true;
          toast("Default due date successfully saved", {
            type: "success",
          });
        } catch {
          toast("Something went wrong while saving the default due date", {
            type: "error",
          });
          setIsSavingDraft(false);
          return;
        }
      }

      if (isActiveChanged) {
        try {
          if (draftIsActive) {
            await activateWorkType({ id: workType.id }).unwrap();
            toast("Work type successfully activated", {
              type: "success",
            });
          } else {
            await deactivateWorkType({ id: workType.id }).unwrap();
            toast("Work type successfully deactivated", {
              type: "success",
            });
          }
          shouldRefreshWorkType = true;
        } catch {
          toast("Something went wrong while updating work type status", {
            type: "error",
          });
          setIsSavingDraft(false);
          return;
        }
      }

      try {
        if (shouldRefreshWorkType) {
          await fetchWorkType();
        }
      } catch {
        toast("Something went wrong while refreshing work type", {
          type: "error",
        });
        setIsSavingDraft(false);
        return;
      }

      if (!shouldRefreshWorkType) {
        setIsSavingDraft(false);
        return;
      }

      setIsSavingDraft(false);
      return;
    }

    try {
      const createdWorkType = await addWorkType({
        name: formData.name,
        description: formData.description,
        isActive: true,
        orgId: getLinkedFirm()?.orgId,
        locationRelayId: getLinkedFirm()?.locationId || null,
      }).unwrap();

      toast("Work type successfully created", {
        type: "success",
      });
      shouldBypassUnsavedPromptRef.current = true;
      navigate(`/settings/work-types/view/${createdWorkType.id}`);
    } catch (error) {
      toast(serverErrorMessage(error, "Something went wrong"), {
        type: "error",
      });
      setIsSavingDraft(false);
    }
  };

  const handleDeleteWorkType = async () => {
    if (!workType) {
      return;
    }

    try {
      await deleteWorkType({ id: workType.id }).unwrap();
      toast("Work type successfully deleted", {
        type: "success",
      });
      setOpenWorkTypeDelete(false);
      shouldBypassUnsavedPromptRef.current = true;
      navigate("/settings/work-types");
    } catch (error) {
      toast(
        serverErrorMessage(
          error,
          "Something went wrong. You can deactivate the work type to hide it",
        ),
        {
          type: "error",
        },
      );
    }
  };

  useEffect(() => {
    if (isEditing) {
      void fetchWorkType();
      void fetchStatuses();
    }
  }, [fetchStatuses, fetchWorkType, isEditing]);

  useEffect(() => {
    if (workType) {
      setValue("name", workType.name);
      setValue("description", workType.description || "");
      setValue(
        "defaultTemplateId",
        workType.defaultWorkTypeData?.defaultTemplateId ?? null,
      );
      setValue(
        "defaultAssigneeId",
        workType.defaultWorkTypeData?.defaultAssigneeId ?? null,
      );
      setValue(
        "defaultRoleId",
        workType.defaultWorkTypeData?.defaultRoleId ?? null,
      );
      setValue(
        "defaultBudget",
        workType.defaultWorkTypeData?.defaultBudget === null ||
          workType.defaultWorkTypeData?.defaultBudget === undefined
          ? "0"
          : String(workType.defaultWorkTypeData?.defaultBudget),
      );
      setValue("billableDefault", toBillableChoice(workType.billableDefault));
      setValue("defaultDueDays", dueDaysToInput(workType.noOfDays));
      setDraftIsActive(workType.isActive);

      setWorkTypeStatuses(getEditableWorkTypeStatuses());
    }
  }, [getEditableWorkTypeStatuses, setValue, workType]);

  useEffect(() => {
    if (!hasUnsavedChanges || isSavingDraft) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, isSavingDraft]);

  return {
    isLoading,
    headerProps: {
      title: isEditing ? workType?.name || "Unknown" : "Add new work type",
      isEditing,
      isSaving:
        isLoadingUpdate ||
        isLoadingUpdateStatuses ||
        isLoadingUpdateDefaultData ||
        isLoadingBillableDefault ||
        isLoadingDefaultDueDays ||
        isLoadingAdd ||
        isLoadingDeactivate ||
        isLoadingActivate,
      canShowActions: isEditing && Boolean(workType),
      isActive: draftIsActive,
      onBack: () => navigate(-1),
      onCancel: () => navigate(-1),
      onSave: () => handleSubmit(onSubmit)(),
      // The ⋮ menu only changes the switch; say so (QA E7).
      onToggleActive: () => {
        const next = !draftIsActive;
        setDraftIsActive(next);
        toast(
          `Click Save Changes to ${
            next ? "activate" : "deactivate"
          } this work type.`,
          { type: "info" },
        );
      },
      onDelete: () => setOpenWorkTypeDelete(true),
    },
    detailsProps: {
      control,
      isEditing,
      isActive: draftIsActive,
      workType,
      onActiveChange: setDraftIsActive,
      isActiveChanged,
    },
    automationProps: {
      isVisible: isEditing && Boolean(workType),
      workType,
      onRefresh: fetchWorkType,
    },
    statusesProps: {
      isVisible: isEditing,
      workTypeId: workType?.id,
      visibleServiceStatuses,
      statuses: statuses || [],
      workTypeStatuses,
      isLoadingStatuses,
      setWorkTypeStatuses,
      getStatuses: fetchStatuses,
    },
    modalProps: {
      workType,
      isDeleteOpen: openWorkTypeDelete,
      isDeleting: isLoadingDelete,
      onCloseDelete: () => setOpenWorkTypeDelete(false),
      onConfirmDelete: () => void handleDeleteWorkType(),
      blocker,
    },
  };
};
